import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Download, Plus, MapPin, Calendar, Pencil, Trash2, Building2, ClipboardList, HardHat, Truck, Receipt,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, downloadCsv, cx } from "@/lib/api";
import { eur, fmtDate, STATO_LABELS, STATO_BADGE } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import TransactionModal from "@/components/TransactionModal";
import CantiereModal from "@/components/CantiereModal";

export default function CantiereDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isOwner = user?.role === "owner";
  const [cantiere, setCantiere] = useState(null);
  const [txs, setTxs] = useState([]);
  const [categories, setCategories] = useState(null);
  const [fornitori, setFornitori] = useState([]);
  const [txModal, setTxModal] = useState(false);
  const [editTx, setEditTx] = useState(null);
  const [cantiereModal, setCantiereModal] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = useCallback(async () => {
    try {
      const [c, t, cats, forn] = await Promise.all([
        api.get(`/cantieri/${id}`),
        api.get("/transactions", { cantiere_id: id, limit: 200 }),
        api.get("/categories"),
        api.get("/fornitori"),
      ]);
      setCantiere(c); setTxs(t.items); setCategories(cats); setFornitori(forn);
    } catch {
      toast.error("Cantiere non trovato.");
      navigate("/cantieri");
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = async () => {
    try {
      await downloadCsv(`/cantieri/${id}/export`, `cantiere_${cantiere?.nome || id}.csv`);
      toast.success("Report esportato in CSV.");
    } catch { toast.error("Esportazione non riuscita."); }
  };

  const exportRapportini = async () => {
    try {
      await downloadCsv("/rapportini/export", `rapportini_${cantiere?.nome || id}.csv`, { cantiere_id: id });
      toast.success("Rapportini esportati in CSV.");
    } catch { toast.error("Esportazione non riuscita."); }
  };

  const confirmDeleteTx = async () => {
    try {
      await api.del(`/transactions/${toDelete.id}`);
      toast.success("Transazione eliminata.");
      setToDelete(null); load();
    } catch (err) { toast.error(err.message); }
  };

  if (!cantiere) return <div className="h-64 rounded-xl bg-secondary animate-pulse" />;

  const budgetUsato = cantiere.budget_stimato > 0 ? Math.min(100, (cantiere.costi / cantiere.budget_stimato) * 100) : 0;

  const entrate = txs.filter((t) => t.tipo === "entrata");
  const uscite = txs.filter((t) => t.tipo === "uscita");
  const colMano = uscite.filter((t) => t.categoria_key === "stipendi_manodopera");
  const colMezzi = uscite.filter((t) => t.categoria_key === "noleggio_attrezzature");
  const colFatture = uscite.filter((t) => !["stipendi_manodopera", "noleggio_attrezzature"].includes(t.categoria_key));
  const sum = (arr) => arr.reduce((s, t) => s + (t.importo || 0), 0);

  const txActions = (t) => (
    <div className="flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
      <Button variant="ghost" size="icon" className="h-6 w-6" data-testid={`edit-tx-${t.id}`} onClick={() => { setEditTx(t); setTxModal(true); }}><Pencil className="h-3 w-3" /></Button>
      {isOwner && <Button variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-destructive" data-testid={`delete-tx-${t.id}`} onClick={() => setToDelete(t)}><Trash2 className="h-3 w-3" /></Button>}
    </div>
  );

  const renderCol = (title, Icon, list, testid) => (
    <Card data-testid={testid} className="flex flex-col">
      <CardHeader className="pb-2 flex-row items-center justify-between">
        <CardTitle className="text-sm flex items-center gap-2 text-muted-foreground"><Icon className="h-4 w-4" /> {title}</CardTitle>
        <span className="font-mono text-sm font-semibold text-red-600">{eur(sum(list))}</span>
      </CardHeader>
      <CardContent className="space-y-1.5 pt-0">
        {list.length === 0 ? (
          <p className="py-6 text-center text-xs text-muted-foreground">Nessuna voce</p>
        ) : list.map((t) => (
          <div key={t.id} data-testid={`cantiere-tx-row-${t.id}`} className="group flex items-start justify-between gap-2 rounded-lg border px-3 py-2 hover:bg-secondary/60 transition-colors">
            <div className="min-w-0">
              <p className="truncate text-sm font-medium">{t.descrizione}</p>
              <p className="text-[11px] text-muted-foreground">{fmtDate(t.data)} · {t.categoria_label}</p>
            </div>
            <div className="flex flex-col items-end gap-1 shrink-0">
              <span className="font-mono text-sm font-semibold text-red-600">-{eur(t.importo)}</span>
              {txActions(t)}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/cantieri")} data-testid="back-to-cantieri"
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors">
        <ArrowLeft className="h-4 w-4" /> Torna ai Cantieri
      </button>

      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">{cantiere.nome}</h1>
            <span className={cx("rounded-full border px-2.5 py-0.5 text-[11px] font-medium", STATO_BADGE[cantiere.stato])}>
              {STATO_LABELS[cantiere.stato]}
            </span>
          </div>
          <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
            <span className="flex items-center gap-1"><Building2 className="h-4 w-4" />{cantiere.codice_commessa || "—"}</span>
            {cantiere.cliente && <span>Cliente: {cantiere.cliente}</span>}
            {cantiere.indirizzo && <span className="flex items-center gap-1"><MapPin className="h-4 w-4" />{cantiere.indirizzo}</span>}
            {cantiere.data_fine_presunta && <span className="flex items-center gap-1"><Calendar className="h-4 w-4" />Fine: {fmtDate(cantiere.data_fine_presunta)}</span>}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setCantiereModal(true)} data-testid="edit-cantiere-detail-button" className="gap-2"><Pencil className="h-4 w-4" /> Modifica</Button>
          <Button variant="outline" onClick={exportCsv} data-testid="export-cantiere-csv-button" className="gap-2"><Download className="h-4 w-4" /> Report CSV</Button>
          <Button variant="outline" onClick={exportRapportini} data-testid="export-rapportini-csv-button" className="gap-2"><ClipboardList className="h-4 w-4" /> Rapportini CSV</Button>
        </div>
      </div>

      <div className={cx("grid grid-cols-2 gap-4", isOwner ? "lg:grid-cols-4" : "lg:grid-cols-3")}>
        {isOwner && <Card><CardContent className="p-5"><p className="text-xs uppercase text-muted-foreground">Ricavi</p><p className="mt-1 text-xl font-bold font-mono text-emerald-600">{eur(cantiere.ricavi)}</p><p className="text-[11px] text-muted-foreground">fatture in entrata</p></CardContent></Card>}
        <Card><CardContent className="p-5"><p className="text-xs uppercase text-muted-foreground">Costi</p><p className="mt-1 text-xl font-bold font-mono text-red-600">{eur(cantiere.costi)}</p></CardContent></Card>
        <Card><CardContent className="p-5"><p className="text-xs uppercase text-muted-foreground">Margine</p><p className={cx("mt-1 text-xl font-bold font-mono", cantiere.margine >= 0 ? "text-emerald-600" : "text-red-600")}>{eur(cantiere.margine)}</p><p className="text-xs text-muted-foreground">Budget − Costi − Spese Gen. · {cantiere.margine_pct}%</p></CardContent></Card>
        <Card><CardContent className="p-5"><p className="text-xs uppercase text-muted-foreground">Budget Commessa</p><p className="mt-1 text-xl font-bold font-mono">{eur(cantiere.budget_stimato)}</p><Progress value={budgetUsato} className="h-1.5 mt-2" /><p className="text-[11px] text-muted-foreground mt-1">{budgetUsato.toFixed(0)}% costi su budget</p></CardContent></Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card data-testid="cantiere-col-manodopera"><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-muted-foreground"><HardHat className="h-4 w-4" /> Manodopera</CardTitle></CardHeader><CardContent className="px-5 pb-5 pt-0"><p className="text-2xl font-bold font-mono">{eur(cantiere.manodopera)}</p></CardContent></Card>
        <Card data-testid="cantiere-col-fatture"><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-muted-foreground"><Receipt className="h-4 w-4" /> Fatture</CardTitle></CardHeader><CardContent className="px-5 pb-5 pt-0"><p className="text-2xl font-bold font-mono">{eur((cantiere.fatture || 0) + (cantiere.subappaltatori || 0))}</p><p className="text-[11px] text-muted-foreground mt-1">di cui subappaltatori {eur(cantiere.subappaltatori)}</p></CardContent></Card>
        <Card data-testid="cantiere-col-mezzi"><CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2 text-muted-foreground"><Truck className="h-4 w-4" /> Mezzi</CardTitle></CardHeader><CardContent className="px-5 pb-5 pt-0"><p className="text-2xl font-bold font-mono">{eur(cantiere.mezzi)}</p></CardContent></Card>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        <Card><CardContent className="p-5"><p className="text-xs uppercase text-muted-foreground">Spese Generali (15%)</p><p className="mt-1 text-xl font-bold font-mono text-amber-600">{eur(cantiere.spese_generali)}</p><p className="text-[11px] text-muted-foreground">15% sui costi</p></CardContent></Card>
        <Card><CardContent className="p-5"><p className="text-xs uppercase text-muted-foreground">Costi + Spese Gen.</p><p className="mt-1 text-xl font-bold font-mono text-red-600">{eur((cantiere.costi || 0) + (cantiere.spese_generali || 0))}</p><p className="text-[11px] text-muted-foreground">sottratti dal budget</p></CardContent></Card>
      </div>

      <Card>
        <CardContent className="p-5 space-y-1">
          <div className="flex justify-between text-sm"><span className="font-medium">Stato Avanzamento Lavori</span><span className="font-mono">{cantiere.sal_percentuale}%</span></div>
          <Progress value={cantiere.sal_percentuale} className="h-3" />
        </CardContent>
      </Card>

      {isOwner && (
      <Card>
        <CardHeader className="pb-2 flex-row items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">Entrate (Ricavi) <span className="font-mono text-sm font-semibold text-emerald-600">{eur(sum(entrate))}</span></CardTitle>
          <Button size="sm" onClick={() => { setEditTx(null); setTxModal(true); }} data-testid="add-cantiere-tx-button"
            className="gap-1.5 bg-slate-900 hover:bg-slate-800 dark:bg-amber-500 dark:hover:bg-amber-600 dark:text-slate-900"><Plus className="h-4 w-4" /> Aggiungi</Button>
        </CardHeader>
        <CardContent className="space-y-1.5">
          {entrate.length === 0 ? (
            <p className="py-4 text-center text-xs text-muted-foreground">Nessuna entrata registrata.</p>
          ) : entrate.map((t) => (
            <div key={t.id} data-testid={`cantiere-tx-row-${t.id}`} className="group flex items-center justify-between gap-2 rounded-lg border px-3 py-2 hover:bg-secondary/60 transition-colors">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{t.descrizione}</p>
                <p className="text-[11px] text-muted-foreground">{fmtDate(t.data)} · {t.categoria_label}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="font-mono text-sm font-semibold text-emerald-600">+{eur(t.importo)}</span>
                {txActions(t)}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
      )}

      <div>
        <div className="mb-2 flex items-center justify-between">
          <p className="text-sm font-medium text-muted-foreground">Uscite del Cantiere</p>
          <Button size="sm" onClick={() => { setEditTx(null); setTxModal(true); }} data-testid="add-cantiere-uscita-button"
            className="gap-1.5 bg-slate-900 hover:bg-slate-800 dark:bg-amber-500 dark:hover:bg-amber-600 dark:text-slate-900"><Plus className="h-4 w-4" /> Aggiungi</Button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="cantiere-tx-columns">
          {renderCol("Manodopera", HardHat, colMano, "col-manodopera")}
          {renderCol("Fatture / Subappaltatori", Receipt, colFatture, "col-fatture")}
          {renderCol("Mezzi", Truck, colMezzi, "col-mezzi")}
        </div>
      </div>

      <TransactionModal open={txModal} onOpenChange={setTxModal} categories={categories} cantieri={[cantiere]} fornitori={fornitori} initial={editTx} lockCantiereId={id} onSaved={load} />
      <CantiereModal open={cantiereModal} onOpenChange={setCantiereModal} initial={cantiere} onSaved={load} />

      <AlertDialog open={Boolean(toDelete)} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare la transazione?</AlertDialogTitle>
            <AlertDialogDescription>L'azione è irreversibile.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteTx} className="bg-destructive hover:bg-destructive/90">Elimina</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
